import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import {
  Box,
  IconBuilding,
  IconLockOutline,
  IconRocket
} from '@machobear/design-system'
import clsx from 'clsx'
import toInteger from 'lodash/toInteger'
import { Marker as MaplibreMarker } from 'react-map-gl/maplibre'

import { mapZIndex } from '@/features/ListingsMap/lib'
import CurrencyFormatter from '@/components/currency-formatter'

import * as styles from './default-marker.css'

type Props = {
  position: [number, number]
  properties?: {
    count: number
    price: number
    is_group: boolean
    buildings: number[]
    sponsored: boolean
    show_label: boolean
  }
  buildingId?: string
  onClick: (position: [number, number], properties: any) => void
  hidePrice?: boolean
  visited?: boolean
  showHoverEffect?: boolean
  locale: string
  showLabels: boolean
}

const DefaultMarker = ({
  position,
  properties,
  buildingId,
  showHoverEffect,
  onClick,
  hidePrice,
  visited,
  locale,
  showLabels
}: Props) => {
  const { t } = useTranslation()
  const {
    count = 0,
    price,
    is_group,
    buildings = [],
    sponsored,
    show_label
  } = properties ?? {}

  const isSingleListing = Number(count) === 1
  const isLocked = hidePrice && isSingleListing
  const isSponsored = sponsored && isSingleListing
  const isBuilding = buildings.length === 1 && !isSingleListing
  const isDot = !showLabels && !show_label

  const unitCount = useMemo(() => {
    return t('filters:unit count', {
      defaultValue: '{{count}} units',
      count: toInteger(count)
    })
  }, [count, t])

  const formattedPrice = isLocked ? (
    t('filters:Locked')
  ) : (
    <CurrencyFormatter
      value={price}
      locale={locale}
      suffix={is_group ? '+' : ''}
    />
  )

  const markerId = useMemo(() => {
    if (isDot) return 'map-marker-dot'
    if (isLocked) return 'map-marker-locked'
    if (isSponsored) return 'map-marker-sponsored'
    if (isBuilding) return 'map-marker-building'
    return 'map-marker-price'
  }, [isLocked, isSponsored, isBuilding, isDot])

  const iconElement = (() => {
    if (isBuilding) return <IconBuilding />
    if (isLocked) return <IconLockOutline />
    if (isSponsored) return <IconRocket />
    return undefined
  })()

  const markerStyle = useMemo(
    () => ({
      zIndex: isDot
        ? mapZIndex.dotMarker
        : isSponsored
        ? mapZIndex.sponsoredMarker
        : mapZIndex.labelMarker
    }),
    [isDot, isSponsored]
  )

  const iconStyle = useMemo(
    () =>
      clsx(
        styles.icon,
        isDot ? styles.dot : styles.single,
        showHoverEffect && styles.hoverEvent[isDot ? 'dot' : 'single'],
        !isDot && isLocked && styles.locked,
        !isDot && isSponsored && styles.sponsored,
        visited && styles.visited
      ),
    [isDot, showHoverEffect, visited, isLocked, isSponsored]
  )

  const handleClick = useCallback(() => {
    onClick(position, { ...properties, building_id: buildingId })
  }, [position, properties, buildingId, onClick])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleClick()
        e.preventDefault()
      }
    },
    [handleClick]
  )

  return (
    <MaplibreMarker
      longitude={position[1]}
      latitude={position[0]}
      offset={isDot ? undefined : [0, -8]}
      style={markerStyle}
    >
      <Box
        tabIndex={0}
        id={markerId}
        role="button"
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        className={iconStyle}
      >
        {!isDot && (
          <span className={styles.content}>
            {iconElement}
            {isBuilding ? unitCount : formattedPrice}
          </span>
        )}
      </Box>
    </MaplibreMarker>
  )
}

export default memo(DefaultMarker)
